import React, {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router";
import {
    doneNavProgress,
    NAV_PROGRESS_DONE,
    NAV_PROGRESS_START,
    startNavProgress,
    wrapHistoryForNavProgress,
} from "../lib/navigation-progress";

const SAFETY_MS = 12000;

function clearBusy(el) {
    el?.removeAttribute("data-fd-nav-busy");
    document.querySelectorAll("[data-fd-nav-busy]").forEach((node) => {
        node.removeAttribute("data-fd-nav-busy");
    });
}

function routeKeyFromLocation() {
    return `${window.location.pathname}?${window.location.search.replace(/^\?/, "")}`;
}

export default function NavigationProgress() {
    const location = useLocation();
    const [active, setActive] = useState(false);
    const busyRef = useRef(null);
    const safetyRef = useRef(null);
    const startedAtRef = useRef(0);
    const hideTimerRef = useRef(null);
    const settleTimerRef = useRef(null);
    const prevKeyRef = useRef(
        `${location.pathname}?${String(location.search || "").replace(/^\?/, "")}`
    );

    const finish = () => {
        const hide = () => {
            setActive(false);
            document.body.classList.remove("fd-nav-pending");
            clearBusy(busyRef.current);
            busyRef.current = null;
        };

        if (safetyRef.current != null) {
            window.clearTimeout(safetyRef.current);
            safetyRef.current = null;
        }

        const elapsed = Date.now() - startedAtRef.current;
        const wait = startedAtRef.current ? Math.max(0, 180 - elapsed) : 0;
        if (hideTimerRef.current != null) {
            window.clearTimeout(hideTimerRef.current);
        }
        if (wait > 0 && document.body.classList.contains("fd-nav-pending")) {
            hideTimerRef.current = window.setTimeout(hide, wait);
            return;
        }
        hide();
    };

    const begin = (target) => {
        if (hideTimerRef.current != null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        if (settleTimerRef.current != null) {
            window.clearTimeout(settleTimerRef.current);
            settleTimerRef.current = null;
        }
        startedAtRef.current = Date.now();
        setActive(true);
        document.body.classList.add("fd-nav-pending");
        if (target) {
            clearBusy(busyRef.current);
            busyRef.current = target;
            target.setAttribute("data-fd-nav-busy", "");
        }
        if (safetyRef.current != null) {
            window.clearTimeout(safetyRef.current);
        }
        safetyRef.current = window.setTimeout(() => {
            doneNavProgress();
        }, SAFETY_MS);
    };

    useEffect(() => {
        const key = `${location.pathname}?${String(location.search || "").replace(/^\?/, "")}`;
        if (prevKeyRef.current === key) {
            return;
        }
        prevKeyRef.current = key;
        if (settleTimerRef.current != null) {
            window.clearTimeout(settleTimerRef.current);
        }
        settleTimerRef.current = window.setTimeout(() => {
            finish();
        }, 280);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    useEffect(() => {
        const onStart = () => begin();
        const onDone = () => finish();

        const restoreHistory = wrapHistoryForNavProgress(window.history, window.location, () => {
            startNavProgress();
        });

        const onPopState = () => {
            if (routeKeyFromLocation() === prevKeyRef.current) {
                return;
            }
            startNavProgress();
        };

        window.addEventListener(NAV_PROGRESS_START, onStart);
        window.addEventListener(NAV_PROGRESS_DONE, onDone);
        window.addEventListener("popstate", onPopState);

        return () => {
            restoreHistory();
            window.removeEventListener(NAV_PROGRESS_START, onStart);
            window.removeEventListener(NAV_PROGRESS_DONE, onDone);
            window.removeEventListener("popstate", onPopState);
            finish();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!active) return null;

    return (
        <div className="fd-nav-progress" role="progressbar" aria-label="در حال بارگذاری" aria-busy="true">
            <div className="fd-nav-progress__bar"/>
        </div>
    );
}
